import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * Header actions component that manages cart notifications and the
 * cart-drawer trigger's `aria-expanded` state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.#syncCartTriggerAriaExpanded();
    this.#setupAccountDialog();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
  }

  #setupAccountDialog() {
    const trigger = this.querySelector('[data-zyra-account-trigger]');
    const dialog = this.querySelector('[data-zyra-account-dialog]');
    const close = this.querySelector('[data-zyra-account-close]');
    const form = this.querySelector('[data-zyra-account-form]');

    if (!(trigger instanceof HTMLElement) || !(dialog instanceof HTMLDialogElement)) return;

    trigger.addEventListener('click', () => {
      if (!dialog.open) dialog.showModal();
      trigger.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => dialog.querySelector('input[type="email"]')?.focus());
    });

    const closeDialog = () => {
      if (dialog.open) dialog.close();
      trigger.setAttribute('aria-expanded', 'false');
    };

    close?.addEventListener('click', closeDialog);
    dialog.addEventListener('close', () => trigger.setAttribute('aria-expanded', 'false'));
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });

    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!(form instanceof HTMLFormElement) || !form.reportValidity()) return;

      const emailInput = form.querySelector('input[type="email"]');
      const email = emailInput instanceof HTMLInputElement ? emailInput.value.trim() : '';
      const loginUrl = form.getAttribute('data-login-url');
      if (!email || !loginUrl) return;

      const url = new URL(loginUrl, window.location.origin);
      url.searchParams.set('login_hint', email);
      window.location.assign(url.toString());
    });
  }

  #syncCartTriggerAriaExpanded = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    const trigger = this.querySelector('[aria-controls="cart-drawer"]');
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', cartDrawer.hasAttribute('open') ? 'true' : 'false');
  };

  /**
   * Syncs `aria-expanded` on the cart-drawer trigger when the drawer opens or closes.
   * @param {Event} event
   */
  #onDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#syncCartTriggerAriaExpanded();
    this.#setupAccountDialog();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const cartCount = cart?.totalQuantity;
        if (cartCount === undefined) return;

        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[header-actions] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
